import * as Yup from 'yup';
import { Formik } from 'formik';
import React, { Fragment } from 'react';
import { ModalComponent } from 'components/layout/modals/ModalComponent';
import { DateInput } from 'components/layout/form-fields/inputs/DateInput';
import { shift } from '@floating-ui/dom';
import { applicantStore } from '../ApplicantStore';
import { eventManager } from 'ats/utils/EventManager';
import { FormField as FormikFormField } from 'components/layout/form-fields/FormField';
import { UsersDropdown } from 'components/layout/form-fields/inputs/UsersDropdown';
import { FormField } from 'components/layout/modals/components';
import { FormColumn } from 'components/layout/modals/components/FormColumn';
import { ApplicantDropdown } from 'components/layout/form-fields/inputs/ApplicantDropdown';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { getApplicantTaskDropdownOptions } from 'ats/utils/applicantTaskTheme';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { formatTimeAgo } from 'shared/utils';
import { NON_CANDIDATE_USER_ROLES } from 'shared/security/RolesAllowed';

const ValidationSchema = Yup.object().shape({
	description: Yup.string().label('Description'),
	type: Yup.object().label('Type').required(),
	scheduledDate: Yup.string().label('Due on').nullable(),
	assignments: Yup.array().nullable().label('Assignments')
});

export class ApplicantTaskEdit extends ModalComponent {
	static isV2 = true;

	getInitialValues() {
		const taskOption = applicantStore.getTaskByValue(this.props.task.type);

		return {
			description: this.props.task.description || '',
			type: taskOption
				? getApplicantTaskDropdownOptions([taskOption])[0]
				: null,
			scheduledDate: this.props.task.scheduledDate || null,
			assignments: this.props.task?.assignments || []
		};
	}

	render() {
		return (
			<Formik
				initialValues={this.getInitialValues()}
				validationSchema={ValidationSchema}
				onSubmit={values => {
					this.saving();
					this.update(values);
				}}>
				{formikBag => this.renderModalContainer(formikBag, 'ApplicantTaskEdit')}
			</Formik>
		);
	}

	renderForm(formikBag) {
		return (
			<Fragment>
				<FormField label="Task" required error={formikBag.errors.type}>
					<Dropdown
						value={formikBag.values.type}
						onChange={item => {
							formikBag.setFieldValue('type', item);
						}}
						options={getApplicantTaskDropdownOptions(
							applicantStore.taskOptions
						)}
						menuClassName="job-task-type-menu"
						placeholder=" "
						disabled
					/>
				</FormField>
				{applicantStore.schedulableTaskTypes.includes(
					formikBag.values.type?.value
				) ? (
					<FormField
						label="Scheduled Date"
						error={formikBag.errors.scheduledDate}>
						<DateInput
							value={
								formikBag.values.scheduledDate
									? new Date(formikBag.values.scheduledDate)
									: null
							}
							popperPlacement="top-start"
							popperModifiers={[
								shift({
									padding: 5,
									crossAxis: true
								})
							]}
							onBlur={() => formikBag.setFieldTouched('scheduledDate')}
							onChange={newDate => {
								formikBag.setFieldValue('scheduledDate', newDate);
							}}
							isDateTime
						/>
					</FormField>
				) : null}
				<FormField label="Candidate">
					<ApplicantDropdown
						placeholder="Candidate"
						value={this.props.applicant}
						showAvatar
						disabled
					/>
				</FormField>
				<FormField label="Assigned to" error={formikBag.errors.assignments}>
					<UsersDropdown
						value={formikBag.values.assignments}
						isMulti
						placeholder=" "
						role={NON_CANDIDATE_USER_ROLES}
						onValueChange={values => {
							formikBag.setFieldValue('assignments', values);
						}}
						showAvatar
					/>
				</FormField>
				<FormColumn>
					<label className="form-field-label-outside">Description</label>
					<FormField error={formikBag.errors.description}>
						<FormikFormField
							className="has-text-left"
							field="description"
							type="textarea"
							placeholder="Description"
							maxlength={2500}
						/>
					</FormField>
				</FormColumn>
				<div className="task-edit-audit-row">
					<span className="task-edit-audit-item">
						<span className="task-edit-audit-label">Created</span>
						<UserProfileAvatar
							imageUrl={
								this.props.task?.createdByUser?.imageUrl ??
								this.props.task?.createdBy?.imageUrl
							}
							name={
								this.props.task?.createdByUser?.name ??
								this.props.task?.createdBy?.name
							}
							tooltipPosition="top"
						/>
						<span className="task-edit-audit-date">
							{this.props.task?.createdAt
								? formatTimeAgo(new Date(this.props.task.createdAt), {
										moreThanOneDay: true
									})
								: '—'}
						</span>
					</span>
					<span className="task-edit-audit-item">
						<span className="task-edit-audit-label">Updated</span>
						<UserProfileAvatar
							imageUrl={this.props.task?.updatedBy?.imageUrl}
							name={this.props.task?.updatedBy?.name}
							tooltipPosition="top"
						/>
						<span className="task-edit-audit-date">
							{this.props.task?.updatedAt
								? formatTimeAgo(new Date(this.props.task.updatedAt), {
										moreThanOneDay: true
									})
								: '—'}
						</span>
					</span>
					{this.props.task?.status !== 'pending' && (
						<span className="task-edit-audit-item">
							<span className="task-edit-audit-label">Closed</span>
							<UserProfileAvatar
								imageUrl={
									this.props.task?.closedByUser?.imageUrl ??
									this.props.task?.closedBy?.imageUrl
								}
								name={
									this.props.task?.closedByUser?.name ??
									this.props.task?.closedBy?.name
								}
								tooltipPosition="top"
							/>
							<span className="task-edit-audit-date">
								{this.props.task?.closedOn
									? formatTimeAgo(new Date(this.props.task.closedOn), {
											moreThanOneDay: true
										})
									: '—'}
							</span>
						</span>
					)}
				</div>
			</Fragment>
		);
	}

	async update(values) {
		applicantStore
			.updateTask(this.props.applicant.id, this.props.task.id, {
				applicantTask: {
					description: values.description,
					scheduledDate: applicantStore.schedulableTaskTypes.includes(
						values.type?.value
					)
						? values.scheduledDate
						: null,
					assignments: values.assignments?.map(assignment => assignment?.id)
				}
			})
			.then(result => {
				if (result.status === 'success') {
					eventManager.emit('taskChanged');
				}
				this.resultNotification(result, 'Task', 'Update');
			});
	}
}
